/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { AsyncResultStatus, getUrlSync } from '@msdyn365-commerce/core';
import {
    Address, AsyncResult, CardPaymentAcceptResult, CardType, CartLine,
    CartsDataActions, StoreOperationsDataActions, TokenizedPaymentCard
} from '@msdyn365-commerce/retail-proxy';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, INodeProps, Modal } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { get } from 'lodash';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { AsyncResultStatusCode } from '../../common';
import { AddPaymentFormComponent } from '../../common/add-payment-form';
import { ErrorComponent } from '../../common/error';
import { Iframe } from '../../common/iframe';
import {
    paymentConnectorExtraContextMessage, PaymentConnectorPostMessageType,
    paymentConnectorSubmitMessage
} from '../../common/payment-instrument-message';
import { WaitingComponent } from '../../common/waiting';
import {
    IVisibilityObserverProps,
    withVisibilityObserver } from '../../common/with-visibility-observer';
import getCardPaymentAcceptPointAction, {
    GetCardPaymentAcceptPointInput
} from './actions/get-card-payment-accept-point';
import { ICheckoutExpressData } from './checkout-express.data';
import { ICheckoutExpressProps } from './checkout-express.props.autogenerated';

/**
 * Interface for CheckOutExpress Module Props.
 */
export interface ICheckoutExpressModuleProps extends ICheckoutExpressProps<ICheckoutExpressData>, IModuleStateProps, IVisibilityObserverProps { }

/**
 * Checkout Express State.
 */
interface ICheckoutExpressState {
    errorMessage?: string;
    isFetchingPaymentConnector?: boolean;
    paymentConnectorHeight?: number;
    isPaymentVerificationRequired?: boolean;
    paymentVerificationPostData?: string;
    isPaymentProcessing?: boolean;
    isOverlayModal?: boolean;
}

/**
 * Checkout Express Overlay Modal.
 */
export interface ICheckoutExpressOverlayModal {
    modal: INodeProps;
}

/**
 * Checkout Express View Props.
 */
export interface ICheckoutExpressViewProps extends ICheckoutExpressProps<{}>, ICheckoutExpressState {
    className?: string;
    checkoutExpress: IModuleProps;
    waiting?: React.ReactNode;
    alert?: React.ReactNode;
    addPaymentForm?: React.ReactNode;
    overlayModal?: ICheckoutExpressOverlayModal;
    isVisible?: boolean;
}

const zeroAmount = 0;

const moduleClassName = 'ms-checkout-express';

/**
 *
 * CheckoutExpress component.
 * @extends {React.Component<ICheckoutExpressProps<ICheckoutExpressData>>}
 */
// @ts-expect-error - existing code.
@withModuleState
@observer
export class CheckoutExpress extends React.PureComponent<ICheckoutExpressModuleProps> {

    @computed public get amountDue(): number {
        const {
            data: {
                checkout
            }
        } = this.props;
        if (!checkout.result) {
            return 0;
        }

        const checkoutResult = checkout.result;
        const cart = checkoutResult.checkoutCart.cart;
        if (!cart.CartLines || !ArrayExtensions.hasElements(cart.CartLines)) {
            return 0;
        }

        // Use the express checkout for all amount.
        return (cart.TotalAmount ?? zeroAmount);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Public value.
    @computed get isCartContainsItemsForShipping(): boolean {
        const pickupDeliveryModeCode = this.props.context.request.channel?.PickupDeliveryModeCode;
        const emailDeliveryModeCode = this.props.context.request.channel?.EmailDeliveryModeCode;

        const { channelDeliveryOptionConfig, featureState } = this.props.data;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const isRetailMultiplePickUpOptionEnabled = featureState.result
            ?.find(feature => feature.Name === multiplePickupStoreSwitchName)?.IsEnabled;
        const cartLines = this.props.data.checkout.result?.checkoutCart.cart.CartLines ?? [];

        return isRetailMultiplePickUpOptionEnabled ? cartLines.some(
            (cartLine: CartLine) => (cartLine.DeliveryMode !== channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(
                deliveryMode => deliveryMode === cartLine.DeliveryMode) && cartLine.DeliveryMode !== emailDeliveryModeCode)
        ) : cartLines.some(
            (cartLine: CartLine) => (cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode)
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Public value.
    @computed get isCartHasSelectedDeliveryMethods(): boolean {
        const cartLines = this.props.data.checkout.result?.checkoutCart.cart.CartLines ?? [];

        return cartLines.every(
            (cartLine: CartLine) => !!cartLine.DeliveryMode
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Public value.
    @computed get asyncResultStatus(): AsyncResultStatus | undefined {
        const isLoading = Object.values(this.props.data).some(data => get(data, 'status') === AsyncResultStatusCode.LOADING);
        if (isLoading) {
            return AsyncResultStatusCode.LOADING;
        }

        const isSuccess = Object.values(this.props.data).every(data => get(data, 'status') === AsyncResultStatusCode.SUCCESS);
        if (isSuccess) {
            return AsyncResultStatusCode.SUCCESS;
        }

        const isFailed = Object.values(this.props.data).some(data => get(data, 'status') === AsyncResultStatusCode.FAILED);
        if (isFailed) {
            return AsyncResultStatusCode.FAILED;
        }

        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Public value.
    @computed get hasSelectedItem(): boolean {
        return !!get(this.props.data, 'checkoutState.result.tokenizedPaymentCard');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Public value.
    @computed get isPaymentVerificationRedirection(): boolean {
        const { requestFormData, query } = this.props.context.request;
        return !!((requestFormData && query && query.pv === '1'));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Public value.
    @computed get requestUrlOrigin(): string {
        const origin = window.location.origin;
        const propsRequestUrl = this.props.context.request.url.requestUrl;
        const requestUrl =
            typeof propsRequestUrl === 'string' ? new URL(propsRequestUrl) : propsRequestUrl;

        return origin || requestUrl.origin;
    }

    // eslint-disable-next-line react/state-in-constructor -- Existing code.
    public state: ICheckoutExpressState = {
        isFetchingPaymentConnector: true
    };

    private readonly moduleClassName: string = 'ms-checkout-payment-instrument';

    private readonly iframeRef: React.RefObject<Iframe> = React.createRef();

    /**
     * Handle payment result.
     * @param resultAccessCode - Result access code.
     * @param isRedirectedFromPaymentGateway - Is redirected.
     */
    @action
    private readonly handlePaymentResult = (resultAccessCode: string, isRedirectedFromPaymentGateway: boolean = false): void => {
        const {
            data: {
                checkout,
                cardPaymentAcceptPoint
            },
            config: {
                isPrimaryPayment,
                paymentTenderType
            },
            resources: { termsAndConditionErrorMessage }
        } = this.props;

        const cartId = checkout.result?.checkoutCart.cart.Id ?? '';
        const paymentConnectorId = cardPaymentAcceptPoint.result?.PaymentConnectorId;

        const settings = {
            ReturnUrl: this.getReturnUrl(),
            ...(paymentConnectorId && { PaymentConnectorId: paymentConnectorId })
        };

        if (checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout) {
            const cardPaymentAcceptResult: CardPaymentAcceptResult = {
                AccessCode: resultAccessCode,
                Settings: settings
            };

            checkout.result.updateCardPaymentAcceptResult({ newCardPaymentAcceptResult: cardPaymentAcceptResult })
                // eslint-disable-next-line promise/prefer-await-to-then -- Not async in calling method.
                .then(async () => {
                    if (!isPrimaryPayment) {
                        if (checkout.result?.isTermsAndConditionChecked || checkout.result?.isTermsAndConditionAccepted) {
                            this.props.moduleState.onSkip();
                            await checkout.result.updateIsPaymentSectionContainerReady({ newIsPaymentSectionContainerReady: true });
                        } else {
                            this.props.moduleState.setHasError(true);
                            this.setErrorMessage(termsAndConditionErrorMessage);
                        }
                    } else {
                        // In case of PSD2 redirect, cart we be updated in server side so refresh cart.
                        if (isRedirectedFromPaymentGateway) {
                            this.props.data.checkout.result?.checkoutCart.refreshCart({});
                        }

                        this.props.moduleState.setHasError(false);
                        this.props.moduleState.onReady();
                        this.setState({
                            terminalError: null,
                            errorMessage: null
                        });
                        this.props.moduleState.setIsSubmitContainer(true);
                    }

                    return AsyncResult.resolve();
                })
                // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- Existing code.
                .catch(async (error: Error) => {
                    // IMPORTANT: Call RS for getting a new payment session. It should not re-use the previous session for the security reason.
                    await this.updatePaymentAcceptPageData();
                    this.setTerminalError(error);
                });

            return;
        }

        // eslint-disable-next-line promise/catch-or-return -- Existing code.
        CartsDataActions.retrieveCardPaymentAcceptResultAsync(
            { callerContext: this.props.context.actionContext }, resultAccessCode, [], cartId, settings)
            // eslint-disable-next-line promise/prefer-await-to-then -- Existing code.
            .then(async cardPaymentAcceptResult => {
                // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-shadow -- Public name.
                const { TenderLine, TokenizedPaymentCard, AdditionalContext } = cardPaymentAcceptResult;

                await this.saveBillingAddress(TokenizedPaymentCard);

                // eslint-disable-next-line promise/always-return -- Existing code.
                if (AdditionalContext) {
                    this.handleAdditionalContext(AdditionalContext);
                } else {
                    if (!TokenizedPaymentCard) {
                        this.setTerminalError(new Error('No TokenizedPaymentCard found'));
                        return;
                    }
                    const checkoutState = this.props.data.checkout.result;
                    if (checkoutState) {
                        // Try to update card prefix, if it is not set already.
                        // Adyen connector does not send msax-cc-cardprefix message
                        // but it sets the card prefix in tokenized card
                        if (!checkoutState.cardPrefix) {
                            const cardPrefix = TokenizedPaymentCard.CardTokenInfo?.MaskedCardNumber;
                            if (cardPrefix) {
                                await checkoutState.updateCardPrefix({ newCardPrefix: cardPrefix });
                            }
                        }

                        if (!TokenizedPaymentCard.CardTypeId) {
                            const cardTypeId = await this.getCardTypeId(checkoutState.cardPrefix ?? '');
                            if (!cardTypeId) {
                                const error = new Error('The specified card type is not supported.');
                                error.name = 'CARDTYPENOTFOUND';
                                this.setTerminalError(error);
                                return;
                            }

                            TokenizedPaymentCard.CardTypeId = cardTypeId;
                        }

                        await checkoutState.updateTenderLine({ newTenderLine: TenderLine });
                        await checkoutState.updateTokenizedPaymentCard({ newTokenizedPaymentCard: TokenizedPaymentCard });
                        await checkoutState.updatePaymentTenderType({ newPaymentTenderType: paymentTenderType });

                        // In case of PSD2 redirect, cart we be updated in server side so refresh cart.
                        if (isRedirectedFromPaymentGateway) {
                            this.props.data.checkout.result?.checkoutCart.refreshCart({});
                        }

                        this.props.moduleState.setHasError(false);
                        this.props.moduleState.onReady();
                        this.setState({
                            terminalError: null,
                            errorMessage: null
                        });
                        this.props.moduleState.setIsSubmitContainer(true);
                    }
                }
            })
            // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- .
            .catch(async (error: Error) => {
                // IMPORTANT: Call RS for getting a new payment session. It should not re-use the previous session for the security reason.
                await this.updatePaymentAcceptPageData();
                this.setTerminalError(error);
            })
            // eslint-disable-next-line promise/prefer-await-to-then -- Existing code.
            .finally(() => {
                this.setState({
                    isPaymentProcessing: false
                });
            });
    };

    /**
     * Handle payment card prefix.
     * @param cardPrefix - Card pre fix.
     */
    @action
    private readonly handlePaymentCardPrefix = async (cardPrefix: string): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;
        if (checkoutState) {
            await checkoutState.updateCardPrefix({ newCardPrefix: cardPrefix });
        }
    };

    /**
     * Handle payment redirect.
     * @param redirectData - Redirect data.
     */
    @action
    private readonly handlePaymentRedirect = (redirectData: string): void => {
        this.setState({
            isPaymentVerificationRequired: true,
            paymentVerificationPostData: redirectData
        });
        this.props.moduleState.setHasError(false);
        this.props.moduleState.onReady();
    };

    /**
     * Init.
     */
    @action
    private readonly init = (): void => {
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            onContainerReady: this.onContainerReady,
            isCancellable: false,
            status: 'disabled'
        });

        const {
            data: {
                checkout
            },
            config
        } = this.props;

        if (this.isPaymentVerificationRedirection &&
            !(checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout && !checkout.result.isPlaceOrderEnabledInRedirection)) {
            if (config.paymentTenderType === checkout.result?.paymentTenderType) {
                const { requestFormData } = this.props.context.request;

                // eslint-disable-next-line deprecation/deprecation -- keep the existing code.
                const formData = btoa(JSON.stringify(requestFormData));

                this.setState({
                    isPaymentProcessing: true
                });

                this.props.moduleState.onPending();
                this.handlePaymentResult(formData, true);
            }
        } else if (this.asyncResultStatus === AsyncResultStatusCode.FAILED) {
            this.setState({
                isFetchingPaymentConnector: false
            });
            this.setTerminalError(new Error('Failed in load data'));
        }
    };

    /**
     * Set terminal error.
     * @param terminalError - The terminal error.
     */
    @action
    private readonly setTerminalError = (terminalError: Error): void => {
        this.props.telemetry.exception(terminalError);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        const {
            resources: { genericErrorMessage, cardTypeErrorMessage }
        } = this.props;
        let errorMessage = genericErrorMessage;

        if (terminalError.name === 'CARDTYPENOTFOUND') {
            errorMessage = cardTypeErrorMessage;
        }

        this.setState({
            terminalError,
            errorMessage
        });
    };

    /**
     * Set error message.
     * @param errorMessage - The error message.
     */
    @action
    private readonly setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            errorMessage
        });
    };

    /**
     * Toggle payment.
     */
    @action
    private readonly togglePayment = () => {
        if (this.props.moduleState.isDisabled) {
            this.props.moduleState.onUpdating();
        } else {
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onDisable();
        }
    };

    /**
     * On submit.
     */
    @action
    private readonly onSubmit = (): void => {
        const { isPrimaryPayment = true } = this.props.config;
        if (isPrimaryPayment) {
            this.postMessageToIframe(paymentConnectorSubmitMessage());
        } else {
            this.props.moduleState.onSkip();
        }
    };

    /**
     * On cancel action.
     */
    @action
    private readonly onCancel = (): void => {
        if (this.hasSelectedItem) {
            this.props.moduleState.onReady();
        } else {
            this.props.moduleState.onUpdating();
        }
    };

    /**
     * On edit action.
     */
    @action
    private readonly onEdit = (): void => {
        this.props.moduleState.onUpdating();

        // IMPORTANT: Call RS for getting a new payment session. It should not re-use the previous session for the security reason.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Existing code.
        this.updatePaymentAcceptPageData();
    };

    /**
     * On container ready action.
     */
    @action
    private readonly onContainerReady = (): void => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Existing code.
        this.redirectToPaymentVerification();
        this.props.moduleState.setIsSubmitContainer(false);
    };

    public componentDidMount(): void {
        when(
            () => this.asyncResultStatus !== AsyncResultStatusCode.LOADING,
            () => {
                this.init();
            }
        );
        reaction(
            () => this.asyncResultStatus !== AsyncResultStatusCode.FAILED,
            () => {
                this.togglePayment();
            }
        );

        // Reaction for new checkout flow.
        if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout) {
            reaction(
                () => this.amountDue,
                () => {
                    if (this.props.data.checkout.result?.shouldCollapsePaymentSection === false &&
                        this.amountDue > zeroAmount && !this.isPaymentVerificationRedirection) {
                        this.onEdit();
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.shouldCollapsePaymentSection,
                () => {
                    if (this.props.data.checkout.result?.shouldCollapsePaymentSection === false && !this.isPaymentVerificationRedirection) {
                        this.onEdit();
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.redirectAdditionalContext,
                () => {
                    if (this.props.config.isPrimaryPayment && this.props.data.checkout.result?.redirectAdditionalContext) {
                        this.onEdit();
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.isPlaceOrderEnabledInRedirection,
                () => {
                    if (this.props.config.isPrimaryPayment && this.props.data.checkout.result?.isPlaceOrderEnabledInRedirection) {
                        this.init();
                    }
                }
            );
        }
    }

    public render(): JSX.Element | null {
        const { errorMessage,
            isFetchingPaymentConnector,
            paymentConnectorHeight,
            isPaymentVerificationRequired: isPaymentVerificationRequried,
            isPaymentProcessing,
            isOverlayModal } = this.state;
        const {
            moduleState: { isReady, hasError, hasInitialized, isPending, hasExternalSubmitGroup },
            config: { iFrameHeightOverride, paymentStyleOverride, className, paymentTenderType },
            resources,
            data: { cardPaymentAcceptPoint },
            visibilityObserver
        } = this.props;

        const isVisible = visibilityObserver?.isVisible;

        if (!hasInitialized || (this.asyncResultStatus !== AsyncResultStatusCode.FAILED)) {
            this.props.context.telemetry.error('Checkout payment content is empty, module wont render');
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Property on existing type.
        const { AcceptPageUrl, AcceptPageContent, MessageOrigin } = cardPaymentAcceptPoint.result ?? {
            AcceptPageUrl: undefined,
            AcceptPageContent: undefined,
            MessageOrigin: undefined
        };

        const paymentTenderTypeClassName = paymentTenderType ? `${this.moduleClassName}-${paymentTenderType.toLowerCase()}` : '';

        const viewProps: ICheckoutExpressViewProps = {
            ...this.props,
            ...this.state,
            isVisible,
            className,

            checkoutExpress: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, paymentTenderTypeClassName, className)
            },
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Double check.
            waiting: !isReady && this.asyncResultStatus !== AsyncResultStatusCode.FAILED &&
            (this.asyncResultStatus === AsyncResultStatusCode.LOADING || isFetchingPaymentConnector || isPending) && (
                <WaitingComponent {...{ message: resources.loadingMessage, className: moduleClassName }} />
            ),
            alert: hasError && errorMessage &&
            <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage, className: moduleClassName }} />,
            addPaymentForm: (!isReady || isPaymentVerificationRequried) && !isPaymentProcessing && (AcceptPageUrl || AcceptPageContent) && isVisible && (
                <AddPaymentFormComponent
                    acceptPageUrl={AcceptPageUrl}
                    acceptPageContent={AcceptPageContent}
                    messageOrigin={MessageOrigin}
                    onSubmit={this.onSubmit}
                    onCancel={this.onCancel}
                    onIFrameMessage={this.oniFrameMessage}
                    iframeRef={this.iframeRef}
                    canSubmit={!hasExternalSubmitGroup}
                    canCancel={!hasExternalSubmitGroup && this.hasSelectedItem}
                    iFrameHeightOverride={iFrameHeightOverride ?? paymentConnectorHeight}
                    requestUrlOrigin={this.requestUrlOrigin}
                    isFetchingPaymentConnector={isFetchingPaymentConnector}
                    paymentStyleOverride={paymentStyleOverride}
                    className={moduleClassName}
                    moduleName='checkout-express'
                    resources={resources}
                />
            ),
            overlayModal: {
                modal: {
                    tag: Modal,
                    className: `${this.moduleClassName}__overlay-modal`,
                    isOpen: isOverlayModal
                }
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * On iFrame has message.
     * @param event - Message event.
     * @returns Promise.
     */
    private readonly oniFrameMessage = async (event: MessageEvent): Promise<void> => {
        let result;
        try {
            if (typeof event.data !== 'string') {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Event is any.
            result = JSON.parse(event.data);
        } catch (error) {
            this.setTerminalError(error as Error);
            return;
        }

        const shouldCheckConnectorId = this.props.config.shouldCheckConnectorId;

        const paymentConnectorId = this.props.data.cardPaymentAcceptPoint.result?.PaymentConnectorId;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Jason parse result is any.
        if (!result || !result.type || (shouldCheckConnectorId && result.id !== paymentConnectorId)) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Jason parse result is any.
        const { type, value } = result;

        switch (type) {
            case PaymentConnectorPostMessageType.Height:
                // The payment connector will post height after it completes initialization
                this.setState({
                    isFetchingPaymentConnector: false,
                    paymentConnectorHeight: Number(value)
                });
                return;
            case PaymentConnectorPostMessageType.Result:
                this.handlePaymentResult(String(value));
                return;
            case PaymentConnectorPostMessageType.CardPrefix:
                await this.handlePaymentCardPrefix(String(value));
                return;
            case PaymentConnectorPostMessageType.Error:
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Jason parse result is any.
                this.handlePaymentError(value);
                return;
            case PaymentConnectorPostMessageType.Redirect:
                this.handlePaymentRedirect(String(value));
                return;
            case PaymentConnectorPostMessageType.Showoverlay:
                this.showOverlayModal(true);
                return;
            case PaymentConnectorPostMessageType.Hideoverlay:
                this.showOverlayModal(false);
                break;

            default:

                // Do nothing

        }
    };

    /**
     * Show overlay modal.
     * @param isShow - Is show.
     */
    private readonly showOverlayModal = (isShow: boolean): void => {
        this.setState({ isOverlayModal: isShow });
    };

    /**
     * Get card type id.
     * @param cardPrefix - Card prefix.
     * @returns Card type id.
     */
    private readonly getCardTypeId = async (cardPrefix: string = ''): Promise<string | undefined> => {
        try {
            const response = await StoreOperationsDataActions.resolveCardTypesAsync(
                { callerContext: this.props.context.actionContext }, cardPrefix, CardType.Unknown);
            if (ArrayExtensions.hasElements(response)) {
                return response[0].TypeId;
            }
        } catch (error) {
            this.props.context.telemetry.error('Call to resolveCardTypesAsync failed.', error);
        }
        return undefined;
    };

    /**
     * Redirect to payment verification.
     * @returns Void.
     */
    private readonly redirectToPaymentVerification = async (): Promise<void> => {
        const { isPaymentVerificationRequired: isPaymentVerificationRequried, paymentVerificationPostData } = this.state;
        if (!isPaymentVerificationRequried || !paymentVerificationPostData) {
            return;
        }

        const checkoutState = this.props.data.checkout.result;
        if (checkoutState) {
            await checkoutState.updatePaymentTenderType({ newPaymentTenderType: this.props.config.paymentTenderType });
            await checkoutState.saveDataInStorage({});
        }

        // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unsafe-assignment -- Json parse is any.
        const { url, data } = JSON.parse(paymentVerificationPostData);
        const form = document.createElement('form');

        form.method = 'POST';
        form.action = String(url);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Data is any.
        for (const propertyName of Object.keys(data)) {
            const element = document.createElement('input');
            element.name = propertyName;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Data is any.
            element.value = data[propertyName];
            form.appendChild(element);
        }

        document.body.appendChild(form);
        form.submit();
    };

    /**
     * Handle payment error.
     * @param value - Message.
     */
    private readonly handlePaymentError = (value?: { message: string }[]): void => {
        const {
            resources: { defaultSubmitErrorMessage = 'An error occurred in payment method details. Please try again.' }
        } = this.props;
        if (!value || !ArrayExtensions.hasElements(value)) {
            // Fallback, if no message could be translated from payment accept page response
            this.setErrorMessage(defaultSubmitErrorMessage);
            return;
        }
        this.setErrorMessage(value.map((errorValue: { message: string }) => errorValue.message).join('\n'));
    };

    /**
     * Get return url.
     * @returns The url.
     */
    private readonly getReturnUrl = (): string => {
        let returnUrl = getUrlSync('checkout', this.props.context.actionContext) ?? '';

        // eslint-disable-next-line security/detect-unsafe-regex -- Existing reg.
        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'u');
        const isAbsoluteUrl = absoluteUrlRegExp.test(returnUrl);
        if (!isAbsoluteUrl) {
            returnUrl = `${window.location.origin}${returnUrl}`;
        }

        return `${returnUrl}${!returnUrl.includes('?') ? '?' : '&'}pv=1`;
    };

    /**
     * Update payment accept page data.
     */
    private readonly updatePaymentAcceptPageData = async (): Promise<void> => {
        const {
            context: { actionContext },
            config: { shouldShowBillingAddress, paymentTenderType },
            context: { request: { apiSettings } },
            data: { checkout }
        } = this.props;

        const input = {
            shouldShowBillingAddress,
            paymentTenderType,
            apiSettings,
            shouldEnableSinglePaymentAuthorizationCheckout: checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout,
            amountDue: this.amountDue
        };

        this.setState({
            isFetchingPaymentConnector: true
        });

        const checkoutState = checkout.result;
        if (checkoutState) {
            await checkoutState.updatePaymentTenderType({ newPaymentTenderType: undefined });
            await checkoutState.updateTenderLine({ newTenderLine: undefined });
            await checkoutState.updateTokenizedPaymentCard({ newTokenizedPaymentCard: undefined });
        }

        getCardPaymentAcceptPointAction(new GetCardPaymentAcceptPointInput(input), actionContext)
            // eslint-disable-next-line promise/prefer-await-to-then -- Existing code.
            .then(paymentAcceptPoint => {
                // In case, add form is not hidden. We need to explicity reset value to force re-rendering.
                actionContext.update(new GetCardPaymentAcceptPointInput(input), { AcceptPageContent: '', AcceptPageUrl: '' });

                // IMPORTANT: Do NOT set isFetchingPaymentConnector = false in getCardPaymentAcceptPointAction success response.
                // The payment connector will post a message with content height when payment is ready.
                actionContext.update(new GetCardPaymentAcceptPointInput(input), paymentAcceptPoint);
                this.setState({ paymentConnectorId: paymentAcceptPoint.PaymentConnectorId });

                // For new checkout flow, redirect additionContext comes from Checkout API exception and will be handled in below.
                if (checkoutState?.shouldEnableSinglePaymentAuthorizationCheckout && checkoutState.redirectAdditionalContext) {
                    this.handleAdditionalContext(checkoutState.redirectAdditionalContext);
                }

                return paymentAcceptPoint;
            })
            // eslint-disable-next-line promise/prefer-await-to-callbacks, promise/prefer-await-to-then -- Existing code.
            .catch((error: Error) => {
                this.setTerminalError(error);

                this.setState({
                    isFetchingPaymentConnector: false
                });

                // IMPORTANT: Clear the AcceptPageUrl and AcceptPageContent. It should not re-use the previous session for the security reason.
                actionContext.update(new GetCardPaymentAcceptPointInput(input), { AcceptPageContent: '', AcceptPageUrl: '' });
            });
    };

    /**
     * Post message to iFrame.
     * @param message - The message to post.
     */
    private readonly postMessageToIframe = (message: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Message is any.
        const postMessage = get(this.iframeRef, 'current.postMessage');
        if (postMessage) {
            this.props.moduleState.onPending();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Message is any.
            postMessage(message);
        }
    };

    /**
     * Handle additional context.
     * @param additionalContext - Additional context.
     */
    private readonly handleAdditionalContext = (additionalContext: string): void => {
        const message = paymentConnectorExtraContextMessage(additionalContext);
        this.postMessageToIframe(message);
    };

    /**
     * Save billing address.
     * @param tokenizedPaymentCard - Tokenized payment card.
     */
    private readonly saveBillingAddress = async (tokenizedPaymentCard?: TokenizedPaymentCard): Promise<void> => {
        const { shouldShowBillingAddress } = this.props.config;
        const checkoutState = this.props.data.checkout.result;

        // If we use billing address provided by adyen, billing address returned in TokenizedPaymentCard.
        if (checkoutState && shouldShowBillingAddress && tokenizedPaymentCard && tokenizedPaymentCard.Zip) {
            const billingAddress: Address = {
                ThreeLetterISORegionName: tokenizedPaymentCard.Country,
                Name: tokenizedPaymentCard.House === 'N/A' ? '' : tokenizedPaymentCard.House,
                Street: tokenizedPaymentCard.Address1,
                StreetNumber: tokenizedPaymentCard.Address2,
                City: tokenizedPaymentCard.City,
                State: tokenizedPaymentCard.State,
                ZipCode: tokenizedPaymentCard.Zip,
                Phone: tokenizedPaymentCard.Phone
            };
            await checkoutState.updateBillingAddress({ newBillingAddress: billingAddress });
        }
    };
}

export default withVisibilityObserver(CheckoutExpress, moduleClassName);
